import { GRADES, gradeLabel, isGrade } from "@vavr/core";
// Children: the roster table, and each child's page with adults, restrictions, delegations and
// the event timeline (plan §7 tenant and onboarding, §5 restrictions).
import { useMemo, useState, type ReactElement } from "react";
import { Badge, Banner, Button, Card, Checkbox, Dialog, DialogClose, PhotoTile, Select, Switch, Table, TextField } from "@vavr/ui";
import { api, blobUrl, dateTime, eventLabel, FLAG_LABEL, fullName, Link, navigate } from "../shared/index.ts";
import { useConsole, useGet, useAction, useBlob } from "./data.tsx";
import { eventTime } from "./reports.ts";
import {
  ConfirmButton, FileButton, fromDateInput, KIND_LABEL, LoadError, Loading, RESTRICTION_LABEL, toDateInput, Toolbar, uploadBytes, uploadPhoto, useNames, validity,
} from "./common.tsx";

interface ChildRow { id: string; first: string; last: string; dob: string | null; room_id: string | null; room_name?: string | null; photo_sha256: string | null; status: string; external_ref: string | null; grade?: string | null; room_grade?: string | null; dismissal_plan?: string | null }
interface Room { id: string; name: string; teacher_ids: string[] }
interface LinkedAdult { id: string; first: string; last: string; photo_sha256: string | null; phone: string | null; email: string | null; relation: string; kind: string; valid_from: number | null; valid_to: number | null; can_delegate: number }
interface Restriction { id: string; child_id: string; subject_adult_id: string | null; subject_name: string | null; kind: string; never_release: number; doc_sha256: string | null; valid_from: number | null; valid_to: number | null; notes: string | null }

export function ChildrenList() {
  const { base, can } = useConsole();
  const children = useGet<{ children: ChildRow[] }>(`${base}/children`);
  const rooms = useGet<{ rooms: Room[] }>(`${base}/rooms`, 30_000);
  const [q, setQ] = useState("");
  const [room, setRoom] = useState("all");
  const [status, setStatus] = useState("active");

  const rows = useMemo(() => (children.data?.children ?? []).filter((c) =>
    (!q || `${c.first} ${c.last} ${c.external_ref ?? ""}`.toLowerCase().includes(q.trim().toLowerCase()))
    && (room === "all" || (room === "none" ? !c.room_id : c.room_id === room))
    && (status === "all" || c.status === status)), [children.data, q, room, status]);

  if (children.loading) return <Loading />;
  if (children.error) return <LoadError error={children.error} retry={children.reload} />;
  return (
    <div className="pmu-stack">
      <Toolbar actions={can("roster.write") && <ChildDialog rooms={rooms.data?.rooms ?? []} trigger={<Button icon="plus">Add a child</Button>} />}>
        <TextField label="Search" hideLabel type="search" placeholder="Search by name or ID  ( / )" value={q} onChange={(e) => setQ(e.target.value)} data-search="" />
        <Select label="Room" value={room} onValueChange={(v) => setRoom(v ?? "all")} options={[{ value: "all", label: "All rooms" }, ...(rooms.data?.rooms ?? []).map((r) => ({ value: r.id, label: r.name })), { value: "none", label: "No room" }]} />
        <Select label="Status" value={status} onValueChange={(v) => setStatus(v ?? "active")} options={[{ value: "active", label: "Enrolled" }, { value: "withdrawn", label: "Withdrawn" }, { value: "all", label: "All" }]} />
      </Toolbar>
      <Table
        caption={`${rows.length} ${rows.length === 1 ? "child" : "children"}`} rowKey={(c) => c.id} rows={rows} maxHeight="calc(100dvh - 220px)"
        empty={children.data?.children.length ? "No child matches." : "No children yet. Add one, or import your roster."}
        columns={[
          { key: "name", header: "Name", rowHeader: true, render: (c) => <Link className="pmu-link" href={`/console/children/${c.id}`}>{c.last}, {c.first}</Link> },
          { key: "room", header: "Room", render: (c) => c.room_name ?? <span className="pmu-muted">None</span> },
          { key: "grade", header: "Grade", render: (c) => { const g = c.grade ?? c.room_grade ?? null; return isGrade(g) ? gradeLabel(g) : <span className="pmu-muted">Not set</span>; } },
          { key: "dob", header: "Date of birth", render: (c) => c.dob ?? "" },
          { key: "ref", header: "ID", render: (c) => c.external_ref ?? "" },
          { key: "status", header: "Status", render: (c) => (c.status === "active" ? <Badge tone="success">Enrolled</Badge> : <Badge>Withdrawn</Badge>) },
        ]}
      />
    </div>
  );
}

export function ChildDetail({ id }: { id: string }) {
  const { base, site, can } = useConsole();
  const d = useGet<{ child: ChildRow; adults: LinkedAdult[]; restrictions: Restriction[]; delegations: any[]; events: any[] }>(`${base}/children/${id}`);
  const rooms = useGet<{ rooms: Room[] }>(`${base}/rooms`, 30_000);
  const { names } = useNames();
  const photo = useBlob(base, d.data?.child.photo_sha256);
  const act = useAction();

  if (d.loading) return <Loading />;
  if (d.error || !d.data) return <LoadError error={d.error ?? new Error("Not found")} retry={d.reload} />;
  const { child, adults, restrictions, delegations, events } = d.data;
  const now = Date.now();
  const active = restrictions.filter((r) => (r.valid_to == null || r.valid_to >= now));
  const expired = restrictions.filter((r) => r.valid_to != null && r.valid_to < now);
  const adultName = (aid: string | null) => (aid ? (adults.find((a) => a.id === aid) ? fullName(adults.find((a) => a.id === aid)!) : names.adult(aid)) : "");
  const room = rooms.data?.rooms.find((r) => r.id === child.room_id);
  const invalidate = [`${base}/children`, `${base}/board`];

  return (
    <div className="pmu-stack">
      <p><Link className="pmu-link" href="/console/children">All children</Link></p>
      <Card>
        <div className="pmu-c-profile">
          <PhotoTile kind="child" name={fullName(child)} src={photo} detail={room?.name ?? "No room"} />
          <dl className="pmu-kv">
            <dt>Date of birth</dt><dd>{child.dob ?? "Not recorded"}</dd>
            <dt>Status</dt><dd>{child.status === "active" ? "Enrolled" : "Withdrawn"}</dd>
            {child.external_ref && <><dt>ID</dt><dd>{child.external_ref}</dd></>}
          </dl>
          {can("roster.write") && (
            <div className="pmu-c-profile__actions">
              <ChildDialog child={child} rooms={rooms.data?.rooms ?? []} trigger={<Button variant="secondary">Edit</Button>} />
              <FileButton label={child.photo_sha256 ? "Replace photo" : "Add a photo"} accept="image/*" disabled={act.busy}
                onFile={(f) => act.run(async () => {
                  const sha = await uploadPhoto(base, f);
                  await api("PUT", `${base}/children/${child.id}`, { ...childPayload(child), photo_sha256: sha });
                }, { success: "Photo saved.", invalidate })} />
            </div>
          )}
        </div>
        {act.error && <Banner tone="danger" title={act.error} />}
      </Card>

      {active.map((r) => (
        <Banner key={r.id} tone="restriction" title={`${r.subject_adult_id ? adultName(r.subject_adult_id) : r.subject_name}: ${r.never_release ? "never release" : "check before release"}`}
          actions={r.doc_sha256 ? <Button size="sm" variant="secondary" onClick={async () => window.open(await blobUrl(`${base}/blobs/${r.doc_sha256}`), "_blank", "noopener")}>Open document</Button> : undefined}>
          {RESTRICTION_LABEL[r.kind] ?? r.kind} · {validity(r.valid_from, r.valid_to, site.tz).label}{r.notes ? ` · ${r.notes}` : ""}
        </Banner>
      ))}

      <Card title="Adults" description="Who may collect this child, and who is told when they leave." flush
        actions={can("roster.write") && <LinkAdultDialog childId={child.id} trigger={<Button size="sm" icon="plus">Link an adult</Button>} />}>
        <Table
          caption="Adults linked to this child" hideCaption rowKey={(a) => a.id} rows={adults}
          empty="No adults yet. Link a guardian so someone can collect this child."
          columns={[
            { key: "name", header: "Name", rowHeader: true, render: (a) => <span className="pmu-stack pmu-c-tight"><span>{fullName(a)}</span><span className="pmu-small pmu-muted">{[a.email, a.phone].filter(Boolean).join(" · ")}</span></span> },
            { key: "rel", header: "Relationship", render: (a) => a.relation },
            { key: "kind", header: "Can collect as", render: (a) => <Badge tone={a.kind === "guardian" ? "brand" : "neutral"}>{KIND_LABEL[a.kind] ?? a.kind}</Badge> },
            { key: "valid", header: "Valid", render: (a) => { const v = validity(a.valid_from, a.valid_to, site.tz); return <Badge tone={v.tone}>{v.label}</Badge>; } },
            { key: "del", header: "May delegate", render: (a) => (a.can_delegate ? "Yes" : "No") },
            ...(can("roster.write") ? [{
              key: "act", header: <span className="pmu-sr-only">Actions</span>, align: "end" as const, render: (a: LinkedAdult) => (
                <span className="pmu-row pmu-c-row-actions">
                  <LinkAdultDialog childId={child.id} existing={a} trigger={<Button size="sm" variant="ghost">Edit</Button>} />
                  {a.kind === "guardian" && can("staff.manage") && <InviteGuardian adult={a} />}
                  <ConfirmButton size="sm" variant="ghost" danger title={`Remove ${fullName(a)}?`} description={`${a.first} will no longer be able to collect ${child.first}. Past records are kept.`} confirmLabel="Remove"
                    onConfirm={() => act.run(() => api("DELETE", `${base}/children/${child.id}/adults/${a.id}`), { success: "Adult removed.", invalidate })}>Remove</ConfirmButton>
                </span>
              ),
            }] : []),
          ]}
        />
      </Card>

      <GoingHomeConsents childId={id} first={child.first} />
      <ChildBus childId={id} first={child.first} />
      <Card title="Restrictions" description="Court orders, protective orders and site decisions. Shown at the door on every device." flush
        actions={can("restriction.write") && <RestrictionDialog childId={child.id} adults={adults} trigger={<Button size="sm" icon="plus">Add a restriction</Button>} />}>
        <Table
          caption="Restrictions" hideCaption rowKey={(r) => r.id} rows={[...active, ...expired]} empty="No restrictions."
          columns={[
            { key: "who", header: "Person", rowHeader: true, render: (r) => (r.subject_adult_id ? adultName(r.subject_adult_id) : r.subject_name) },
            { key: "kind", header: "Kind", render: (r) => RESTRICTION_LABEL[r.kind] ?? r.kind },
            { key: "never", header: "Release", render: (r) => (r.never_release ? <Badge tone="restriction">Never release</Badge> : "Check first") },
            { key: "valid", header: "Valid", render: (r) => { const v = validity(r.valid_from, r.valid_to, site.tz); return <Badge tone={v.tone}>{v.label}</Badge>; } },
            { key: "notes", header: "Notes", render: (r) => r.notes ?? "" },
          ]}
        />
      </Card>

      {delegations.length > 0 && (
        <Card title="Delegations" description="One-time or short permissions a guardian gave from the app." flush>
          <Table
            caption="Delegations" hideCaption rowKey={(x) => x.id} rows={delegations}
            columns={[
              { key: "who", header: "Delegate", rowHeader: true, render: (x) => adultName(x.delegate_adult_id) },
              { key: "by", header: "Given by", render: (x) => adultName(x.granted_by_adult_id) },
              { key: "win", header: "Window", render: (x) => `${dateTime(x.valid_from, site.tz)} to ${dateTime(x.valid_to, site.tz)}` },
              { key: "st", header: "Status", render: (x) => (x.status === "revoked" ? <Badge>Revoked</Badge> : x.used_seq ? <Badge tone="info">Used</Badge> : x.valid_to < now ? <Badge>Expired</Badge> : <Badge tone="success">Active</Badge>) },
            ]}
          />
        </Card>
      )}

      <Card title="Timeline" description="The last 100 records for this child, newest first." flush>
        <Table
          caption="Events" hideCaption rowKey={(e) => String(e.seq)} rows={events} empty="Nothing recorded yet."
          columns={[
            { key: "t", header: "When", width: "10rem", render: (e) => <Link className="pmu-link" href={`/console/events/${e.id}`}>{dateTime(eventTime(e), site.tz)}</Link> },
            { key: "y", header: "What", rowHeader: true, render: (e) => eventLabel(e.type) },
            { key: "a", header: "Adult", render: (e) => (e.adult_id ? adultName(e.adult_id) : e.pre?.claimed_name ?? "") },
            { key: "s", header: "Staff", render: (e) => names.staff(e.staff_id) },
            { key: "f", header: "Flags", render: (e) => <span className="pmu-row">{e.server_flags.map((f: string) => <Badge key={f} tone={f === "restriction_hit" ? "restriction" : "warning"}>{FLAG_LABEL[f] ?? f}</Badge>)}</span> },
          ]}
        />
      </Card>
    </div>
  );
}

const childPayload = (c: ChildRow) => ({ first: c.first, last: c.last, dob: c.dob, room_id: c.room_id, external_ref: c.external_ref, status: c.status, photo_sha256: null as string | null });

function ChildDialog({ child, rooms, trigger }: { child?: ChildRow; rooms: Room[]; trigger: ReactElement }) {
  const { base } = useConsole();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(() => ({ first: child?.first ?? "", last: child?.last ?? "", dob: child?.dob ?? "", room_id: child?.room_id ?? "", external_ref: child?.external_ref ?? "", status: child?.status ?? "active", grade: child?.grade ?? "", dismissal_plan: child?.dismissal_plan ?? "" }));
  const act = useAction();
  const save = async () => {
    const body = { ...f, dob: f.dob || null, room_id: f.room_id || null, external_ref: f.external_ref || null, grade: f.grade || null, dismissal_plan: f.dismissal_plan || null };
    const r = await act.run(() => (child ? api("PUT", `${base}/children/${child.id}`, body) : api("POST", `${base}/children`, body)),
      { success: child ? "Saved." : "Child added.", invalidate: [`${base}/children`, `${base}/board`] });
    if (r) { setOpen(false); if (!child) navigate(`/console/children/${r.id}`); }
  };
  return (
    <Dialog title={child ? `Edit ${child.first}` : "Add a child"} open={open} onOpenChange={setOpen} trigger={trigger}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!f.first.trim() || !f.last.trim()} onClick={save}>{child ? "Save" : "Add child"}</Button></>}>
      <form className="pmu-stack" onSubmit={(e) => { e.preventDefault(); void save(); }}>
        {act.error && <Banner tone="danger" title={act.error} />}
        <div className="pmu-grid-2">
          <TextField label="First name" required value={f.first} onChange={(e) => setF({ ...f, first: e.target.value })} autoFocus />
          <TextField label="Last name" required value={f.last} onChange={(e) => setF({ ...f, last: e.target.value })} />
          <TextField label="Date of birth" type="date" value={f.dob} onChange={(e) => setF({ ...f, dob: e.target.value })} />
          <Select label="Room" value={f.room_id || "none"} onValueChange={(v) => setF({ ...f, room_id: v === "none" || !v ? "" : v })} options={[{ value: "none", label: "No room" }, ...rooms.map((r) => ({ value: r.id, label: r.name }))]} />
          <Select label="Grade" hint="Only where the room mixes grades. The school's rules (walking in, the bus stop) follow it." value={f.grade || "room"} onValueChange={(v) => setF({ ...f, grade: v === "room" || !v ? "" : v })}
            options={[{ value: "room", label: "Same as the room" }, ...GRADES.map((g) => ({ value: g, label: gradeLabel(g) }))]} />
          <TextField label="ID in your other system" hint="Optional" value={f.external_ref} onChange={(e) => setF({ ...f, external_ref: e.target.value })} />
          <TextField label="Usually goes home by" hint="Shown to the teacher at dismissal, e.g. Car line, After-school club. Buses and consents show on their own." maxLength={120} value={f.dismissal_plan} onChange={(e) => setF({ ...f, dismissal_plan: e.target.value })} />
          {child && <Select label="Status" value={f.status} onValueChange={(v) => setF({ ...f, status: v ?? "active" })} options={[{ value: "active", label: "Enrolled" }, { value: "withdrawn", label: "Withdrawn" }]} />}
        </div>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}

/** Pick an adult already on file, by typing part of their name or email. */
export function AdultPicker({ value, onChange, exclude = [] }: { value: string | null; onChange: (id: string | null, a?: any) => void; exclude?: string[] }) {
  const { base } = useConsole();
  const adults = useGet<{ adults: any[] }>(`${base}/adults`, 30_000);
  const [q, setQ] = useState("");
  const matches = (adults.data?.adults ?? []).filter((a) => !exclude.includes(a.id) && (!q || `${a.first} ${a.last} ${a.email ?? ""}`.toLowerCase().includes(q.toLowerCase()))).slice(0, 8);
  const chosen = adults.data?.adults.find((a) => a.id === value);
  return (
    <div className="pmu-stack">
      <TextField label="Find an adult on file" type="search" value={q} onChange={(e) => setQ(e.target.value)} hint={chosen ? `Chosen: ${fullName(chosen)}` : "Type part of a name or email"} />
      <div className="pmu-c-picker" role="group" aria-label="Matching adults">
        {matches.map((a) => (
          <button key={a.id} type="button" className="pmu-c-picker__item" aria-pressed={a.id === value} onClick={() => onChange(a.id === value ? null : a.id, a)}>
            <span>{fullName(a)}</span><span className="pmu-small pmu-muted">{a.email ?? a.phone ?? ""}</span>
          </button>
        ))}
        {matches.length === 0 && <span className="pmu-muted pmu-small">No match.</span>}
      </div>
    </div>
  );
}

function LinkAdultDialog({ childId, existing, trigger }: { childId: string; existing?: LinkedAdult; trigger: ReactElement }) {
  const { base, site } = useConsole();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [adultId, setAdultId] = useState<string | null>(existing?.id ?? null);
  const [n, setN] = useState({ first: "", last: "", phone: "", email: "" });
  const [l, setL] = useState(() => ({
    relation: existing?.relation ?? "", kind: existing?.kind ?? "guardian", from: toDateInput(existing?.valid_from, site.tz), to: toDateInput(existing?.valid_to, site.tz),
    can_delegate: existing ? !!existing.can_delegate : true,
  }));
  const act = useAction();
  const ready = l.relation.trim() && (existing || (mode === "existing" ? adultId : n.first.trim() && n.last.trim()));
  const save = async () => {
    const ok = await act.run(async () => {
      let aid = adultId;
      if (!existing && mode === "new") aid = (await api("POST", `${base}/adults`, { first: n.first, last: n.last, phone: n.phone || null, email: n.email || null, photo_sha256: null, notes: null })).id;
      await api("PUT", `${base}/children/${childId}/adults/${aid}`, {
        relation: l.relation, kind: l.kind, valid_from: fromDateInput(l.from, site.tz), valid_to: fromDateInput(l.to, site.tz, true), can_delegate: l.can_delegate,
      });
      return true;
    }, { success: existing ? "Saved." : "Adult linked.", invalidate: [`${base}/children/${childId}`, `${base}/adults`] });
    if (ok) setOpen(false);
  };
  return (
    <Dialog title={existing ? `Edit ${fullName(existing)}` : "Link an adult"} open={open} onOpenChange={setOpen} trigger={trigger}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!ready} onClick={save}>Save</Button></>}>
      <div className="pmu-stack">
        {act.error && <Banner tone="danger" title={act.error} />}
        {!existing && (
          <Select label="Who" value={mode} onValueChange={(v) => setMode(v === "new" ? "new" : "existing")} options={[{ value: "existing", label: "Someone already on file" }, { value: "new", label: "Someone new" }]} />
        )}
        {!existing && mode === "existing" && <AdultPicker value={adultId} onChange={(id) => setAdultId(id)} />}
        {!existing && mode === "new" && (
          <div className="pmu-grid-2">
            <TextField label="First name" required value={n.first} onChange={(e) => setN({ ...n, first: e.target.value })} />
            <TextField label="Last name" required value={n.last} onChange={(e) => setN({ ...n, last: e.target.value })} />
            <TextField label="Mobile phone" type="tel" value={n.phone} onChange={(e) => setN({ ...n, phone: e.target.value })} />
            <TextField label="Email" type="email" value={n.email} onChange={(e) => setN({ ...n, email: e.target.value })} />
          </div>
        )}
        <div className="pmu-grid-2">
          <TextField label="Relationship" required placeholder="Mother, grandfather, neighbor…" value={l.relation} onChange={(e) => setL({ ...l, relation: e.target.value })} />
          <Select label="Can collect as" value={l.kind} onValueChange={(v) => setL({ ...l, kind: v ?? "guardian" })} options={[{ value: "guardian", label: "Guardian (gets the app and notifications)" }, { value: "authorised", label: "Authorized adult" }, { value: "delegate", label: "Delegate" }]} />
          <TextField label="Valid from" type="date" hint="Leave blank for now" value={l.from} onChange={(e) => setL({ ...l, from: e.target.value })} />
          <TextField label="Valid until" type="date" hint="Leave blank for no end" value={l.to} onChange={(e) => setL({ ...l, to: e.target.value })} />
        </div>
        <Checkbox label="May give others permission to collect" checked={l.can_delegate} onCheckedChange={(c) => setL({ ...l, can_delegate: c })} />
      </div>
    </Dialog>
  );
}

function RestrictionDialog({ childId, adults, trigger }: { childId: string; adults: LinkedAdult[]; trigger: ReactElement }) {
  const { base, site } = useConsole();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ kind: "court", subject: "name", adult: null as string | null, name: "", never: true, from: "", to: "", notes: "" });
  const [doc, setDoc] = useState<{ name: string; sha: string } | null>(null);
  const act = useAction();
  const ready = f.subject === "name" ? f.name.trim() : f.adult;
  const save = async () => {
    const ok = await act.run(() => api("POST", `${base}/restrictions`, {
      child_id: childId, kind: f.kind, subject_adult_id: f.subject === "adult" ? f.adult : null, subject_name: f.subject === "name" ? f.name : null,
      never_release: f.never, valid_from: fromDateInput(f.from, site.tz), valid_to: fromDateInput(f.to, site.tz, true), notes: f.notes || null, doc_sha256: doc?.sha ?? null,
    }), { success: "Restriction saved. Devices get it on their next roster sync.", invalidate: [`${base}/children/${childId}`] });
    if (ok) setOpen(false);
  };
  return (
    <Dialog title="Add a restriction" description="Staff see this as the amber RESTRICTION banner at the door." open={open} onOpenChange={setOpen} trigger={trigger}
      actions={<><DialogClose><Button variant="secondary">Cancel</Button></DialogClose><Button loading={act.busy} disabled={!ready} onClick={save}>Save restriction</Button></>}>
      <div className="pmu-stack">
        {act.error && <Banner tone="danger" title={act.error} />}
        <div className="pmu-grid-2">
          <Select label="Kind" value={f.kind} onValueChange={(v) => setF({ ...f, kind: v ?? "court" })} options={Object.entries(RESTRICTION_LABEL).map(([value, label]) => ({ value, label }))} />
          <Select label="The person is" value={f.subject} onValueChange={(v) => setF({ ...f, subject: v ?? "name" })} options={[{ value: "name", label: "Not on file (enter a name)" }, { value: "adult", label: "Already on file" }]} />
        </div>
        {f.subject === "name"
          ? <TextField label="Full name as on the order" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
          : adults.length ? <Select label="Adult" value={f.adult} onValueChange={(v) => setF({ ...f, adult: v })} options={adults.map((a) => ({ value: a.id, label: `${fullName(a)} (${a.relation})` }))} />
            : <AdultPicker value={f.adult} onChange={(id) => setF({ ...f, adult: id })} />}
        <Checkbox label="Never release to this person" description="Staff cannot complete a pickup by this person without an override." checked={f.never} onCheckedChange={(c) => setF({ ...f, never: c })} />
        <div className="pmu-grid-2">
          <TextField label="From" type="date" value={f.from} onChange={(e) => setF({ ...f, from: e.target.value })} />
          <TextField label="Until" type="date" hint="Leave blank if it has no end date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} />
        </div>
        <TextField label="Notes" placeholder="Case number, court, contact" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        <div className="pmu-row">
          <FileButton label={doc ? "Replace document" : "Attach the order (PDF)"} accept="application/pdf" disabled={act.busy}
            onFile={(file) => act.run(async () => setDoc({ name: file.name, sha: await uploadBytes(base, new Uint8Array(await file.arrayBuffer()), "application/pdf") }))} />
          {doc && <span className="pmu-small">{doc.name} attached</span>}
        </div>
      </div>
    </Dialog>
  );
}

function InviteGuardian({ adult }: { adult: LinkedAdult }) {
  const { base } = useConsole();
  const act = useAction();
  if (!adult.email) return <Button size="sm" variant="ghost" disabled title="Add an email address to invite">Invite</Button>;
  return (
    <ConfirmButton size="sm" variant="ghost" title={`Invite ${fullName(adult)} to the guardian app?`} description={`We will email ${adult.email} a link to see pickups and manage who can collect.`} confirmLabel="Send invitation"
      onConfirm={() => act.run(() => api("POST", `${base}/invitations`, { role: "guardian", email: adult.email, adult_id: adult.id, name: fullName(adult) }), { success: `Invitation sent to ${adult.email}.` })}>
      Invite
    </ConfirmButton>
  );
}


/**
 * Going home without an adult (school rules): the parent's consents, which the office can also
 * record from a paper form. The school's grade rules still apply when staff mark the child leaving.
 */
function GoingHomeConsents({ childId, first }: { childId: string; first: string }) {
  const { base, can } = useConsole();
  const q = useGet<{ consents: { kind: string; sibling_child_id: string | null; source: string; created_at: number }[]; siblings: { id: string; first: string; last: string }[] }>(`${base}/children/${childId}/consents`);
  const act = useAction();
  if (!q.data) return null;
  const has = (kind: string, sib: string | null = null) => q.data!.consents.find((c) => c.kind === kind && (c.sibling_child_id ?? null) === sib);
  const src = (c: { source: string } | undefined) => (c ? (c.source === "office" ? "Recorded by the office" : "Given by a parent in the app") : "Not given");
  const set = (body: Record<string, unknown>) => void act.run(() => api("PUT", `${base}/children/${childId}/consents`, body), { success: "Saved.", invalidate: [`${base}/children/${childId}/consents`, `${base}/board`] });
  return (
    <Card title="Going home without an adult" description={`${first}'s parent's consents. Staff can mark ${first} leaving on their own or with a sibling only with one, and only where your rules allow it for the grade.`}>
      <div className="pmu-stack">
        {act.error && <Banner tone="danger" title={act.error} />}
        <Switch label={`${first} may go home on their own`} description={src(has("home_alone"))} checked={!!has("home_alone")} disabled={!can("roster.write") || act.busy}
          onCheckedChange={(on) => set({ kind: "home_alone", on })} />
        {q.data.siblings.map((s) => (
          <Switch key={s.id} label={`${first} may leave with ${s.first} ${s.last}`} description={src(has("sibling", s.id))} checked={!!has("sibling", s.id)} disabled={!can("roster.write") || act.busy}
            onCheckedChange={(on) => set({ kind: "sibling", sibling_child_id: s.id, on })} />
        ))}
        {!q.data.siblings.length && <p className="pmu-small pmu-muted">No brothers or sisters at this school (children who share a guardian).</p>}
      </div>
    </Card>
  );
}

interface BusRoutes {
  routes: { route_id: string; name: string; kind: string; stop: string | null; meet_required: boolean; meet_by: string; meet_reason: string | null; not_riding: { late: boolean; why: string } | null;
    /** Today's bus; `replaces` when another stands in for the usual one (PMU-232). */
    bus?: { name: string; plate: string | null; replaces: { name: string; reason: string | null; until: string } | null } | null }[];
  school_default: boolean; walk_from: string | null;
}

/**
 * The child's buses, for families without the app (school rules): the office takes them off today's
 * bus, or sets who meets them at the stop from a paper form or a phone call. Hidden without buses.
 */
function ChildBus({ childId, first }: { childId: string; first: string }) {
  const { base, can } = useConsole();
  const q = useGet<BusRoutes>(`${base}/children/${childId}/transport`);
  const act = useAction();
  const [note, setNote] = useState("");
  if (!q.data || !q.data.routes.length) return null;
  const reload = { invalidate: [`${base}/children/${childId}/transport`] };
  const meet = q.data.routes[0]!;
  const edit = can("roster.write");
  return (
    <Card title="Bus" description={`${first}'s buses today. For families without the app, the office records what they tell you.`}>
      <div className="pmu-stack">
        {act.error && <Banner tone="danger" title={act.error} />}
        {q.data.routes.map((r) => (
          <div key={r.route_id} className="pmu-row pmu-row--between">
            <span className="pmu-stack pmu-c-tight">
              <b>{r.name}{r.bus ? ` · ${r.bus.name}` : ""}</b>
              {r.bus?.replaces && <span className="pmu-small">Standing in for {r.bus.replaces.name}{r.bus.plate ? `, plate ${r.bus.plate}` : ""}{r.bus.replaces.reason ? `: ${r.bus.replaces.reason}` : ""}</span>}
              <span className="pmu-small pmu-muted">{r.kind === "am" ? "Morning" : r.kind === "pm" ? "Afternoon" : "Trip"}{r.stop ? ` · ${r.stop}` : ""} · {r.not_riding ? (r.not_riding.why === "absent" ? "Absent today, not riding" : "Not on the bus today") : "Riding today"}</span>
            </span>
            {edit && r.not_riding?.why !== "absent" && (r.not_riding
              ? <Button size="sm" variant="secondary" loading={act.busy} onClick={() => void act.run(() => api("DELETE", `${base}/children/${childId}/transport/skip/${r.route_id}`), { success: `${first} is back on the bus.`, ...reload })}>Put back on the bus</Button>
              : <Button size="sm" variant="secondary" loading={act.busy} onClick={() => void act.run(() => api("POST", `${base}/children/${childId}/transport/skip`, { route_id: r.route_id }), { success: "The driver knows.", ...reload })}>Not on the bus today</Button>)}
          </div>
        ))}
        <Select label="At the stop" disabled={!edit || act.busy} value={meet.meet_required ? "meet" : "walk"}
          hint={`${meet.meet_by === "parent" ? "The parent's choice" : meet.meet_by === "office" ? "The office's note" : "Your school's rule"}${meet.meet_reason ? `: “${meet.meet_reason}”` : ""}. The rule: ${q.data.walk_from ? `grade ${q.data.walk_from} and up may walk home` : "an adult meets every child"}.`}
          onValueChange={(v) => void act.run(() => api("PUT", `${base}/children/${childId}/transport/meet`, { meet_required: v !== "walk", reason: note || undefined }), { success: "Saved. The driver sees it.", ...reload })}
          options={[{ value: "meet", label: `An adult meets ${first}` }, { value: "walk", label: `${first} may walk home` }]} />
        <TextField label="Note for a change (optional)" value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} placeholder="Paper form from the parent, 30 Sep" />
      </div>
    </Card>
  );
}
